import { useQuery } from "@tanstack/react-query";
import { Post } from "../../../utils/apiService";
import {
  GetAllAuditLogsReportBody,
  GetAllExternalUsersReportBody,
  GetAllFRAccessLogsReportBody,
  GetAllFRAccessLogsReportResponse,
  GetAllFRErrorsReportBody,
  GetAllFRErrorsReportResponse,
  GetAllLoginUsersReportBody,
  GetAllRatingsReportBody,
  GetAllRatingsReportResponse,
  GetAllVisitorsReportBody,
  GetAllVisitRequestsReportBody,
  GetBlacklistingReportBody,
  GetVisitorsReportCheckInCheckOutByDropdownBody,
} from "../interface";

export const FETCH_REPORT_DETAILS_KEYS = {
  FETCH_ALL_VISIT_REQUESTS_REPORT: "FETCH_ALL_VISIT_REQUESTS_REPORT",
  FETCH_ALL_EXTERNAL_USERS_REPORT: "FETCH_ALL_EXTERNAL_USERS_REPORT",
  FETCH_ALL_AUDIT_LOGS_REPORT: "FETCH_ALL_AUDIT_LOGS_REPORT",
  FETCH_ALL_VISITORS_REPORT: "FETCH_ALL_VISITORS_REPORT",
  FETCH_ALL_FR_ACCESS_LOGS_REPORT: "FETCH_ALL_FR_ACCESS_LOGS_REPORT",
  FETCH_ALL_FR_ERRORS_REPORT: "FETCH_ALL_FR_ERRORS_REPORT",
  FETCH_ALL_RATINGS_REPORT: "FETCH_ALL_RATINGS_REPORT",
  FETCH_GET_BLACKLISTING_REPORT: "FETCH_GET_BLACKLISTING_REPORT",
  FETCH_VISITORS_REPORT_CHECK_IN_BY_DROPDOWN: "FETCH_VISITORS_REPORT_CHECK_IN_BY_DROPDOWN",
  FETCH_VISITORS_REPORT_CHECK_OUT_BY_DROPDOWN: "FETCH_VISITORS_REPORT_CHECK_OUT_BY_DROPDOWN",
};

const getBlacklistingReportAction = async (body: GetBlacklistingReportBody) => {
  return Post({
    url: "/api/Reports/GetBlacklistingReport",
    body,
  });
};

export const useGetBlacklistingReport = (body: GetBlacklistingReportBody) => {
  return useQuery({
    queryKey: [FETCH_REPORT_DETAILS_KEYS.FETCH_GET_BLACKLISTING_REPORT, body],
    queryFn: () => getBlacklistingReportAction(body),
  });
};

const getAllVisitorsReportAction = async (body: GetAllVisitorsReportBody) => {
  return Post({
    url: "/api/Reports/GetCheckedInOutVisitors",
    body,
  });
};

export const useGetAllVisitorsReport = (body: GetAllVisitorsReportBody) => {
  return useQuery({
    queryKey: [FETCH_REPORT_DETAILS_KEYS.FETCH_ALL_VISITORS_REPORT, body],
    queryFn: () => getAllVisitorsReportAction(body),
  });
};

const getAllVisitRequestsReportAction = async (
  body: GetAllVisitRequestsReportBody
) => {
  return Post({
    url: "/api/Reports/GetAllVisitRequest",
    body,
  });
};

export const useGetAllVisitRequestsReport = (
  body: GetAllVisitRequestsReportBody
) => {
  return useQuery({
    queryKey: [FETCH_REPORT_DETAILS_KEYS.FETCH_ALL_VISIT_REQUESTS_REPORT, body],
    queryFn: () => getAllVisitRequestsReportAction(body),
  });
};

const getAllExternalUsersAction = async (
  body: GetAllExternalUsersReportBody
) => {
  return Post({
    url: "/api/Reports/GetAllExternalUsers",
    body,
  });
};

export const useGetAllExternalUsersReport = (
  body: GetAllExternalUsersReportBody
) => {
  return useQuery({
    queryKey: [FETCH_REPORT_DETAILS_KEYS.FETCH_ALL_EXTERNAL_USERS_REPORT, body],
    queryFn: () => getAllExternalUsersAction(body),
  });
};

const getAllAuditLogsAction = async (body: GetAllAuditLogsReportBody) => {
  return Post({
    url: "/api/Reports/GetAllAuditLogs",
    body,
  });
};

export const useGetAllAuditLogsReport = (body: GetAllAuditLogsReportBody) => {
  return useQuery({
    queryKey: [FETCH_REPORT_DETAILS_KEYS.FETCH_ALL_AUDIT_LOGS_REPORT, body],
    queryFn: () => getAllAuditLogsAction(body),
  });
};

const getAllLoginUsersAction = async (body: GetAllLoginUsersReportBody) => {
  return Post({
    url: "/api/Reports/GetUsersLogin",
    body,
  });
};

export const useGetAllLoginUsersReport = (body: GetAllLoginUsersReportBody) => {
  return useQuery({
    queryKey: [FETCH_REPORT_DETAILS_KEYS.FETCH_ALL_AUDIT_LOGS_REPORT, body],
    queryFn: () => getAllLoginUsersAction(body),
  });
};

const getAllFRAccessLogsReportAction = async (
  body: GetAllFRAccessLogsReportBody
) => {
  return Post({
    url: "/api/Reports/GetFRAccessLogs",
    body,
  });
};

export const useGetAllFRAccessLogsReport = (
  body: GetAllFRAccessLogsReportBody
) => {
  return useQuery<GetAllFRAccessLogsReportResponse>({
    queryKey: [FETCH_REPORT_DETAILS_KEYS.FETCH_ALL_FR_ACCESS_LOGS_REPORT, body],
    queryFn: () => getAllFRAccessLogsReportAction(body),
  });
};

const getAllFRErrorsReportAction = async (body: GetAllFRErrorsReportBody) => {
  return Post({
    url: "/api/Reports/GetFRErrorsReport",
    body,
  });
};

export const useGetAllFRErrorsReport = (body: GetAllFRErrorsReportBody) => {
  return useQuery<GetAllFRErrorsReportResponse>({
    queryKey: [FETCH_REPORT_DETAILS_KEYS.FETCH_ALL_FR_ERRORS_REPORT, body],
    queryFn: () => getAllFRErrorsReportAction(body),
  });
};

const getAllRatingsReportAction = async (body: GetAllRatingsReportBody) => {
  return Post({
    url: "/api/Reports/GetRatingsReport",
    body,
  });
};

export const useGetAllRatingsReport = (body: GetAllRatingsReportBody) => {
  return useQuery<GetAllRatingsReportResponse>({
    queryKey: [FETCH_REPORT_DETAILS_KEYS.FETCH_ALL_RATINGS_REPORT, body],
    queryFn: () => getAllRatingsReportAction(body),
  });
};

const getVisitorsReportCheckInByDropdownAction = async (body: GetVisitorsReportCheckInCheckOutByDropdownBody) => {
  return Post({
    url: "/api/Reports/GetVisitorsReportCheckInByDropdown",
    body,
  });
};

export const useGetVisitorsReportCheckInByDropdown = (body: GetVisitorsReportCheckInCheckOutByDropdownBody) => {
  return useQuery({
    queryKey: [FETCH_REPORT_DETAILS_KEYS.FETCH_VISITORS_REPORT_CHECK_IN_BY_DROPDOWN, body],
    queryFn: () => getVisitorsReportCheckInByDropdownAction(body),
  });
};

const getVisitorsReportCheckOutByDropdownAction = async (body: GetVisitorsReportCheckInCheckOutByDropdownBody) => {
  return Post({
    url: "/api/Reports/GetVisitorsReportCheckOutByDropdown",
    body,
  });
};

export const useGetVisitorsReportCheckOutByDropdown = (body: GetVisitorsReportCheckInCheckOutByDropdownBody) => {
  return useQuery({
    queryKey: [FETCH_REPORT_DETAILS_KEYS.FETCH_VISITORS_REPORT_CHECK_OUT_BY_DROPDOWN, body],
    queryFn: () => getVisitorsReportCheckOutByDropdownAction(body),
  });
};